import { createAsyncThunk } from '@reduxjs/toolkit'
import axios, { AxiosResponse } from 'axios'
import { TAgreeTermsValues, TAvailabilitiesFormValues, TBankAccountFormValues, TJobPreferencesFormValues, TNextOfKinFormValues, TPersonalDetailsFormValues, TQualificationsEditFormValues, TQualificationsFormValues } from '../../models/Onboarding/onboarding'
import { TAgreeTermsResponse, TAvailabilities, TAvailabilitiesRepsonse, TBankAccountDetailsResponse, TCheckDocStatusPayload, TCheckDocStatusResponse, TConnectIdResponse, TContractFormResponse, TGetFormFieldsPayload, TGetFormFieldsResponse, TJobPreferencesRepsonse, TMakeARequestResponse, TMyProfileStageSubmitResponse, TPersonalDetailsRepsonse, TProfileEditResponse, TProfileResponse, TQualificationRepsonse, TReferroChecksResponse, TSaveEmergencyInfoResponse, TStartInterviewResponse, TSubmitFormFieldResponse, TSubmitFormFieldsPayload } from './types'

import { FormValues } from '../../models/form'
import { setTokenAction } from '../auth/slice'
import { getCandidatePendingTimesheetThunk } from '../timesheet/thunks'
import { setAvailabilityAction, setBankAccountsDetailAction, setCompletionStatusAction, setContractFormAction, setContractSignedFormUrl, setDocStatus, setFlkFormData, setIsConnectIdVerifiedAction, setJobPreferencesAction, setNextOfKinAction, setPersonalDetailsAction, setProfileStepsAction, setQualificationsAction, setReferralCodeAction, setReopenedStagesStatusAction, setSuperFormAction, setTFNFormAction, setUserPersonalDetailsAction, setViddiMailStatusAction, setViddiReviewStatusAction, setViddiStatusAction } from './slice'

/**
 * GET method
 * getProfileDetailsThunk - Get the details of user and all submitted forms
 */
export const getProfileDetailsThunk = createAsyncThunk(
  'candidates/getProfileDetailsThunk',
  async (data, { dispatch, rejectWithValue }) => {
    try { 
      const responseUserData: AxiosResponse<TProfileResponse> = await axios.get(`/v1/candidates/profile`)
      await dispatch(setTokenAction(responseUserData.data.data.token))
      if (responseUserData.data.data?.user?.role?.id === 5) {
        await dispatch(getCandidatePendingTimesheetThunk())
      }
      dispatch(setUserPersonalDetailsAction(responseUserData.data.data.user))
      dispatch(setPersonalDetailsAction(responseUserData.data.data.user.profile))
      dispatch(setQualificationsAction(responseUserData.data.data.user.qualifications))
      dispatch(setJobPreferencesAction(responseUserData.data.data.user.jobpreference))
      dispatch(setCompletionStatusAction(responseUserData.data.data.user.completion_status))
      dispatch(setViddiMailStatusAction(responseUserData.data.data.user.viddi_mail_status))
      dispatch(setViddiStatusAction(responseUserData.data.data.user.viddi_status))
      dispatch(setViddiReviewStatusAction(responseUserData.data.data.user.viddi_review_status))
      dispatch(setIsConnectIdVerifiedAction(responseUserData.data.data.user.is_connectid_verified))
      dispatch(setNextOfKinAction(responseUserData.data.data.user.emergencyInfo))
      dispatch(setAvailabilityAction(responseUserData.data.data.user.availabilities))
      dispatch(setReferralCodeAction(responseUserData.data.data.user.referral_code))
      dispatch(setProfileStepsAction(responseUserData.data.data.user.profileSteps))
      dispatch(setBankAccountsDetailAction(responseUserData.data.data.user.bankAccount))
      dispatch(setReopenedStagesStatusAction(responseUserData.data.data.user.reopened_stages_status))
      return responseUserData.data
    } catch (err) {
      return rejectWithValue((err as any).response.data)
    }
  }
)

/**
 * Step 2
 * POST method
 * personalDetailsThunk - Submit the Personal Details form
 */
export const personalDetailsThunk = createAsyncThunk(
  'candidates/personalDetailsThunk',
  async (data: TPersonalDetailsFormValues, { rejectWithValue, dispatch }) => {
    try {
      const responsePersonalDetails: AxiosResponse<TPersonalDetailsRepsonse> = await axios.post('/v1/candidates/personal-details', data)
      await dispatch(setTokenAction(responsePersonalDetails.data.data.token))
      if(!data.isSuperAdmin){
        await dispatch(getProfileDetailsThunk())
      }
      // dispatch(getMeThunk())
      return responsePersonalDetails.data
    } catch (err) {
      return rejectWithValue((err as any).response.data || err)
    }
  }
)

/**
 * Step 3
 * POST method
 * qualificationsThunk - Submit the Qualifications form
 */
export const qualificationsThunk = createAsyncThunk(
  'candidates/qualificationsThunk',
  async (data: TQualificationsFormValues | TQualificationsEditFormValues , { rejectWithValue, dispatch }) => {
    try {
      const responseQualification: AxiosResponse<TQualificationRepsonse> = await axios.post('/v1/candidates/qualifications', data)
      await dispatch(setTokenAction(responseQualification.data.data.token))
      if(!data.isSuperAdmin){
        await dispatch(getProfileDetailsThunk())
      }
      return responseQualification.data
    } catch (err) {
      return rejectWithValue((err as any).response.data || err)
    }
  }
)

/**
 * Step 4
 * POST method
 * jobPreferencesThunk - Submit the Job Preferences form
 */
export const jobPreferencesThunk = createAsyncThunk(
  'candidates/jobPreferencesThunk',
  async (data: TJobPreferencesFormValues, { rejectWithValue, dispatch }) => {
    try {
      const responseJobPreferences: AxiosResponse<TJobPreferencesRepsonse> = await axios.post('/v1/candidates/job-preferences', data)
      await dispatch(setTokenAction(responseJobPreferences.data.data.token))
      if(data?.candidate_uuid === "" || data?.candidate_uuid === undefined || data?.isImpersonating === true){
        await dispatch(getProfileDetailsThunk())
      }
      return responseJobPreferences.data
    } catch (err) {
      return rejectWithValue((err as any).response.data || err)
    }
  }
)

/**
 * POST method
 * availabilitiesThunk - Submit the Availabilities form
 */
export const availabilitiesThunk = createAsyncThunk(
  'candidates/availabilitiesThunk',
  async (data: TAvailabilitiesFormValues, { rejectWithValue, dispatch }) => {
    try {
      const responseAvailabilities: AxiosResponse<TAvailabilitiesRepsonse> = await axios.post('/v1/candidates/availabilities', data)
      dispatch(setTokenAction(responseAvailabilities.data.data.token))
      dispatch(setAvailabilityAction(responseAvailabilities.data.data as unknown as TAvailabilities))
      return responseAvailabilities.data
    } catch (err) {
      return rejectWithValue((err as any).response.data || err)
    }
  }
)

/**
 * POST method
 * getFormFieldsThunk - Get the Form fields
 */
export const getFormFieldsThunk = createAsyncThunk(
  'candidates/getFormFieldsThunk',
  async (data: TGetFormFieldsPayload, { rejectWithValue, dispatch }) => {
    try {
      const responseGetFormFields: AxiosResponse<TGetFormFieldsResponse> = await axios.post('/v1/flkitover/getFlkTemplates', data)
      await dispatch(setTokenAction(responseGetFormFields.data.data.token))
      await dispatch(setTFNFormAction(responseGetFormFields.data))
      return responseGetFormFields.data
    } catch (err) {
      return rejectWithValue((err as any).response.data || err)
    }
  }
)

/**
 * POST method
 * submitTFNFormThunk - To submit the TFN form
 */
export const submitTFNForm = createAsyncThunk(
  'candidates/submitTFNFormThunk',
  async (data: TSubmitFormFieldsPayload, { rejectWithValue, dispatch }) => {
    try {
      const storeAPIResponse: AxiosResponse<TSubmitFormFieldResponse> = await axios.post('/v1/flkitover/store', data)
      await dispatch(setTokenAction(storeAPIResponse.data.data.token))
      dispatch(setFlkFormData(storeAPIResponse.data));
      return storeAPIResponse.data
    } catch (err) {
      return rejectWithValue((err as any).response.data || err)
    }
  }
)

/**
 * POST method
 * checkDocStatus - To check the document status
 */
export const checkDocStatus = createAsyncThunk(
  'candidates/checkDocStatus',
  async (data: TCheckDocStatusPayload, { rejectWithValue, dispatch }) => {
    try {
      const docSatusResponse: AxiosResponse<TCheckDocStatusResponse> = await axios.post('/v1/flkitover/checkDocStatus', data)
      // await dispatch(setTokenAction(docSatusResponse.data.data.token))
      await dispatch(getProfileDetailsThunk())
      dispatch(setDocStatus(docSatusResponse.data))
      return docSatusResponse
    } catch (err) {
      return rejectWithValue((err as any).response.data || err)
    }
  }
)

/**
 * POST method
 * getSuperFormFieldsThunk - To get the Super form fields
 */
export const getSuperFormFieldsThunk = createAsyncThunk(
  'candidates/getSuperFormFieldsThunk',
  async (data: TGetFormFieldsPayload, { rejectWithValue, dispatch }) => {
    try {
      const responseGetFormFields: AxiosResponse<TGetFormFieldsResponse> = await axios.post('/v1/flkitover/getFlkTemplates', data)
      await dispatch(setTokenAction(responseGetFormFields.data.data.token))
      await dispatch(setSuperFormAction(responseGetFormFields.data))
      return responseGetFormFields.data
    } catch (err) {
      return rejectWithValue((err as any).response.data || err)
    }
  }
)

/**
 * POST method
 * saveEmergencyInfoThunk - Submit the Next of Kin form
 */
export const saveEmergencyInfoThunk = createAsyncThunk(
  'candidates/saveEmergencyInfoThunk',
  async (data: TNextOfKinFormValues, { rejectWithValue, dispatch }) => {
    try {
      const responseSaveEmergencyInfo: AxiosResponse<TSaveEmergencyInfoResponse> = await axios.post('/v1/candidates/emergency-info', data)
      await dispatch(setTokenAction(responseSaveEmergencyInfo.data.data.token))
      await dispatch(getProfileDetailsThunk())
      return responseSaveEmergencyInfo.data
    } catch (err) {
      return rejectWithValue((err as any).response.data || err)
    }
  }
)

/**
 * GET method
 * startInterviewThunk - click to start interview
 */
export const startInterviewThunk = createAsyncThunk(
  'candidates/startInterviewThunk',
  async (data, { dispatch, rejectWithValue }) => {
    try {
      const response: AxiosResponse<TStartInterviewResponse> = await axios.get(`/v1/viddi/check-status`)
      await dispatch(setTokenAction(response.data.data.token))
      await dispatch(getProfileDetailsThunk())
      return response.data
    } catch (err) {
      return rejectWithValue((err as any).response.data)
    }
  }
)

/**
 * POST method
 * sendConnectIdDetailsThunk - Send to save the connectID details got from API
 */
export const sendConnectIdDetailsThunk = createAsyncThunk(
  'candidates/sendConnectIdDetailsThunk',
  async (data: FormValues, { rejectWithValue, dispatch }) => {
    try {
      const responseSendConnectIdDetails: AxiosResponse<TConnectIdResponse> = await axios.post('/v1/connectid/store-token', data)
      await dispatch(getProfileDetailsThunk())
      return responseSendConnectIdDetails.data
    } catch (err) {
      return rejectWithValue((err as any).response.data || err)
    }
  }
)

/**
 * POST method
 * startReferroChecksThunk - Send request to start referro checks
 */
export const startReferroChecksThunk = createAsyncThunk(
  'candidates/startReferroChecksThunk',
  async ({ isReopen, hasPoliceCheck }: { isReopen?: boolean; hasPoliceCheck?: boolean }, { rejectWithValue, dispatch }) => {
    try {
      const responseStartReferroChecks: AxiosResponse<TReferroChecksResponse> = await axios.post('/v1/referoo/candidate/create-retrieve', { isReopen: isReopen ?? false, hasPoliceCheck: hasPoliceCheck ?? false })
      if (responseStartReferroChecks?.data?.data?.token) {
        dispatch(
          setTokenAction(responseStartReferroChecks.data.data.token)
        );
      }
      // await dispatch(getProfileDetailsThunk())
      return responseStartReferroChecks.data
    } catch (err) {
      return rejectWithValue((err as any).response.data || err)
    }
  }
)

/**
 * Completed profile edit option
 */
export const editCompletedPersonalDetail = createAsyncThunk(
  'candidates/editCompletedPersonalDetail',
  async (data: FormValues, { rejectWithValue, dispatch }) => {
    try {
      const responseEditProfile: AxiosResponse<TProfileEditResponse> = await axios.post('/v1/candidates/update-profile', data)
      await dispatch(setTokenAction(responseEditProfile.data.data.token))
      await dispatch(getProfileDetailsThunk())
      return responseEditProfile.data
    } catch (err) {
      return rejectWithValue((err as any).response.data || err)
    }
  }
)
 
/**
 * POST method
 * Submit Make a Request form
 * profileEditRequestThunk - Submit the Make a Request form
 *
 */
export const profileEditRequestThunk = createAsyncThunk(
  'candidates/profileEditRequestThunk',
  async (data: FormValues, { rejectWithValue, dispatch }) => {
    try {
      const responseEditProfile: AxiosResponse<TMakeARequestResponse> = await axios.post('/v1/candidates/profile-edit-requests', data)
      await dispatch(setTokenAction(responseEditProfile.data.data.token))
      return responseEditProfile.data
    } catch (err) {
      return rejectWithValue((err as any).response.data || err)
    }
  }
)

export const interviewCompleteLaterThunk = createAsyncThunk(
  'candidates/interviewCompleteLaterThunk',
  async (data: FormValues, { rejectWithValue, dispatch }) => {
    try {
      const responseEditProfile: AxiosResponse<TMyProfileStageSubmitResponse> = await axios.post('/v1/candidates/update-stage', data)
      await dispatch(setTokenAction(responseEditProfile.data.data.token))
      return responseEditProfile.data
    } catch (err) {
      return rejectWithValue((err as any).response.data || err)
    }
  }
)


export const myProfileStageSubmit = createAsyncThunk(
  'candidates/myProfileStageSubmit',
  async (data: FormValues, { rejectWithValue, dispatch }) => {
    try {
      const responseEditProfile: AxiosResponse<TMyProfileStageSubmitResponse> = await axios.post('/v1/candidates/update-stage', data)
      await dispatch(setTokenAction(responseEditProfile.data.data.token))
      return responseEditProfile.data
    } catch (err) {
      return rejectWithValue((err as any).response.data || err)
    }
  }
)

export const getContractForm = createAsyncThunk(
  'candidates/getContractForm',
  async (_, { rejectWithValue, dispatch }) => {
    try {
      const responseGetFormFields: AxiosResponse<TContractFormResponse> = await axios.post('/v1/flkitover/contractstore')
      await dispatch(setTokenAction(responseGetFormFields.data.data.token))
      await dispatch(setContractFormAction(responseGetFormFields.data?.data?.flk_client_url))
      await dispatch(setContractSignedFormUrl(responseGetFormFields.data?.data?.s3_pdf_url ?? ""))
      return responseGetFormFields.data
    } catch (err) {
      return rejectWithValue((err as any).response.data || err)
    }
  }
)

/**
 * POST method
 * saveBankAccountThunk - Submit the Bank Account Details form
 */
export const saveBankAccountThunk = createAsyncThunk(
  'candidates/saveBankAccountThunk',
  async (data: TBankAccountFormValues, { rejectWithValue, dispatch }) => {
    try {
      const responseBankAccountDetails: AxiosResponse<TBankAccountDetailsResponse> = await axios.post('/v1/candidates/bank-account', data)
      await dispatch(setTokenAction(responseBankAccountDetails.data.data.token))
      if(data?.candidate_uuid === "" || data?.candidate_uuid === undefined){
        await dispatch(getProfileDetailsThunk())
      }
      return responseBankAccountDetails.data
    } catch (err) {
      return rejectWithValue((err as any).response.data || err)
    }
  }
)

/**
 * POST method
 * Submit Make a Request form
 * profileEditRequestThunk - Submit the Make a Request form
 *
 */
export const profileDocumentRenewRequest = createAsyncThunk(
  'candidates/profileEditRequestThunk',
  async (data: FormValues, { rejectWithValue, dispatch }) => {
    try {
      const responseEditProfile: AxiosResponse<TMakeARequestResponse> = await axios.post('/v1/candidates/key-document-profile-edit-requests', data)
      await dispatch(setTokenAction(responseEditProfile.data.data.token))
      return responseEditProfile.data
    } catch (err) {
      return rejectWithValue((err as any).response.data || err)
    }
  }
)

/**
 * POST method
 * AgreeTermsThunk - Agree the terms of the pdfs
 */
export const AgreeTermsThunk = createAsyncThunk(
  'onboarding/AgreeTermsThunk',
  async (data: TAgreeTermsValues, { rejectWithValue, dispatch }) => {
    try {
      const responseAgreeTerms: AxiosResponse<TAgreeTermsResponse> = await axios.post('/v1/candidates/update-terms-policies', data)
      await dispatch(setTokenAction(responseAgreeTerms.data.data.token))
      await dispatch(getProfileDetailsThunk())
      return responseAgreeTerms.data
    } catch (err) {
      return rejectWithValue((err as any).response.data || err)
    }
  }
)